import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

// @mui material components
import Card from '@mui/material/Card'
import Badge from '@mui/material/Badge'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import MDAlert from 'components/MDAlert'
import CrudService from 'services/cruds-service'
import moment from 'moment/moment'
import { getDuration } from 'utils/utils'
import convertUtils from 'convert-units';
import RouteCollapsibleTable from 'examples/Tables/RouteCollapseTable'
import { Box, CardContent, Modal, Skeleton, TextField } from '@mui/material'
import JsonTree from 'cruds/campaign/modal/jsontree'
import RouteModal from 'cruds/device-management/internal-device-management/view-device/RouteModel'
import RouteMap from 'examples/Maps/RouteMap'
import MDInput from 'components/MDInput'
import { toast } from 'react-toastify'

function Routes() {
    let { state } = useLocation()
    const [data, setData] = useState([])
    const [rawDataValue, setRawDataValue] = useState({})
    const [jsonTree, setJsonTree] = useState(false)
    const [selectedRouteGroup, setSelectedRouteGroup] = useState(null)
    const [highlightRouteData, setHighlightRouteData] = useState([])
    const [openViewModal, setOpenViewModal] = useState(false)
    const [selectedLogInfo, setSelectedLogInfo] = useState(null)
    const [templateValuesList, setTemplateValuesList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [notification, setNotification] = useState({
        value: false,
        text: '',
    })
    // ---------------------------------------------------------------------------
    // Route Table Settings Persistence (mirrors device list settings)
    // ---------------------------------------------------------------------------
    const ROUTE_TABLE_STORAGE_KEY = "rivio.routes.tableSettings.v1";
    const DEFAULT_ROUTE_TABLE_SETTINGS = {
        page: 1,
        rowsPerPage: 25,
        sortModel: { field: "route_id", direction: "desc" },
        filterModel: { items: [] },
        searchQuery: "",
    };
    function readRouteTableSettings() {
        try {
            return {
                ...DEFAULT_ROUTE_TABLE_SETTINGS,
                ...JSON.parse(localStorage.getItem(ROUTE_TABLE_STORAGE_KEY) || "{}"),
            };
        } catch (error) {
            console.error("readRouteTableSettings", error);
            return DEFAULT_ROUTE_TABLE_SETTINGS;
        }
    }
    function writeRouteTableSettings(settings) {
        localStorage.setItem(ROUTE_TABLE_STORAGE_KEY, JSON.stringify(settings));
    }
    const savedRouteSettings = readRouteTableSettings();
    const [searchQuery, setSearchQuery] = useState(savedRouteSettings.searchQuery);
    const [page, setPage] = useState(savedRouteSettings.page);
    const [rowsPerPage, setRowsPerPage] = useState(savedRouteSettings.rowsPerPage);
    const [sortModel, setSortModel] = useState(savedRouteSettings.sortModel);
    const [filterModel, setFilterModel] = useState(savedRouteSettings.filterModel);
    const countRef = useRef(0);

    // Persist route table settings (page, rowsPerPage, sort, filter, search)
    useEffect(() => {
        writeRouteTableSettings({
            page,
            rowsPerPage,
            sortModel,
            filterModel,
            searchQuery,
        });
    }, [page, rowsPerPage, sortModel, filterModel, searchQuery]);

    useEffect(() => {
        (async () => {
            let route_utils = await CrudService.getRoutesUtils();
            setTemplateValuesList(route_utils.route_template_values)
        })()
    }, [])

    useEffect(() => {
        callApi()
        document.title = `Rivio | Routes`
    }, [page, rowsPerPage, sortModel, filterModel])

    async function callApi() {
        setIsLoading(true)
        try {
            const sortField = sortModel?.field || "route_id";
            const sortOrder = sortModel?.direction || "desc";

            const response = await CrudService.getAllRoutes({
                page: page,
                rowsPerPage,
                sortField,
                sortOrder,
                search: searchQuery.trim().toLowerCase(),
                filters: filterModel.items,
            })
            setData(response.data)
            countRef.current = response.count
        } catch (error) {
            console.error('callApi error', error);
            toast.error(error.error || "Failed to fetch routes");
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery !== "") {
                callApi();
            }
        }, 1000)

        return () => clearTimeout(delayDebounceFn)
    }, [searchQuery])

    const searchHandler = async (value) => {
        setSearchQuery(value)
        if (value === "") {
           const response = await CrudService.getAllRoutes({
                page: page,
                rowsPerPage,
                sortField: sortModel?.field || "route_id",
                sortOrder: sortModel?.direction || "desc",
                search: '',
                filters: filterModel.items,
            })
            setData(response.data)
            countRef.current = response.count
        }
    }

    useEffect(() => {
        if (!state) return
        setNotification({
            value: state.value,
            text: state.text,
        })
    }, [state])

    useEffect(() => {
        if (notification.value === true) {
            let timer = setTimeout(() => {
                setNotification({
                    value: false,
                    text: '',
                })
            }, 5000)
        }
    }, [notification])



    const getRows = (info) => {
        let updatedInfo = info.map((row) => {

            if (row.start_group_id) {
                row['start_group'] = templateValuesList.filter((e) => e.condition_group_id == row.start_group_id)
            }
            if (row.end_group_id) {
                row['end_group'] = templateValuesList.filter((e) => e.condition_group_id == row.end_group_id)
            }

            const finalOutput = row?.RouteSummary?.geofence_entries ?? []

            const formatted = finalOutput.filter((e) => e.duration != '0m 0s')
                .map((f) => `${f.name}: ${f.duration}`)
                .join(", ");

            const formattedNames = finalOutput.filter((e) => e.duration != '0m 0s')
                .map(({ name }) => name)
                .join(", ");

            return {
                type: 'routes',
                ...row,
                route_id: row.route_id,
                status: row.StatusType.status_name,
                entity_name: row.Device.EntityAddressItem.Entity.name,
                device_id: row.Device.device_id,
                device_name: row.Device.name,
                entity_address_name: row.Device.EntityAddressItem.name,
                entity_type: row.Device.EntityAddressItem.Item.name,
                start_route: row?.start_time ? moment(row?.start_time).format("MMMM DD, YYYY h:mm:ss A") : '',
                end_route: row?.end_latitude ? (row?.end_time ? moment(row?.end_time).format("MMMM DD, YYYY h:mm:ss A") : '') : '',
                total_distance: row?.total_distance ? `${convertUtils(row?.total_distance).from('km').to('mi').toFixed(2)} miles` : '',
                total_duration: row?.total_duration ? getDuration(row?.total_duration) : '',
                pause_time: row?.RouteSummary?.pause_time ?? '',
                run_time: row?.RouteSummary?.run_time ?? '',
                visited_geofences: formattedNames ?? '',
                total_time_in_geofences: formatted ?? '',
                fuel_used: row?.RouteSummary?.fuel_used ?? '',
                // RouteTrackingPoints: row?.RouteTrackingPoints ?? [],
            }
        })
        return updatedInfo
    }

    const routeColumns = [
        { Header: 'ID', accessor: 'route_id', width: "150px", show: true },
        {
            Header: 'Status', accessor: 'status', width: "200px", show: true, Cell: (info, row) => {
                const badgeDetail = row.status_option.BadgeDetail
                return (
                    <Badge
                        color={badgeDetail.color}
                        variant={badgeDetail.variant}
                        badgeContent={info}
                        style={{ marginLeft: "20px" }}
                    ></Badge>
                )
            }
        },
        { Header: 'Entity Name', accessor: 'entity_name', width: "200px", show: true },
        { Header: 'Device ID', accessor: 'device_id', width: "200px", show: true },
        { Header: 'Device Name', accessor: 'device_name', width: "200px", show: true },
        { Header: 'Item Name', accessor: 'entity_address_name', width: "200px", show: true },
        { Header: 'Item Type', accessor: 'entity_type', width: "200px", show: true },
        { Header: 'Start Route', accessor: 'start_route', width: "200px", show: true },
        { Header: 'End Route', accessor: 'end_route', width: "200px", show: true },
        { Header: 'Visited Geofences', accessor: 'visited_geofences', width: "200px", show: true },
        { Header: 'Total Time(Geofence)', accessor: 'total_time_in_geofences', width: "200px", show: true },
        { Header: 'Total Distance', accessor: 'total_distance', width: "200px", show: true },
        { Header: 'Total Duration', accessor: 'total_duration', width: "200px", show: true },
        { Header: 'Run Time', accessor: 'run_time', width: "200px", show: true },
        { Header: 'Pause Time', accessor: 'pause_time', width: "200px", show: true },
        { Header: 'Fuel Used', accessor: 'fuel_used', width: "200px", show: true },
        {
            Header: "actions",
            disableSortBy: true,
            accessor: "actions",
            show: true,
        },
    ]

    const routeSubColumns = [
        { Header: "ID", accessor: "point_id", show: true },
        { Header: "Status", accessor: "status", show: true },
        { Header: "Latitude", accessor: "latitude", show: true },
        { Header: "Longitude", accessor: "longitude", show: true },
        { Header: "Speed", accessor: "speed", show: true },
        {
            Header: "Heading",
            accessor: "heading",

            show: true,
        },
        { Header: "Geofence", accessor: "geofence_id", show: true },
        { Header: "Geofence Event", accessor: "geofence_event_id", show: true },
        { Header: "Ignition On", accessor: "ignition_on", show: true },
        { Header: "RPM", accessor: "can_engine_rpm", show: true },
        { Header: "Fuel Level", accessor: "can_fuel_level", show: true },
        { Header: "MAP", accessor: "convert_can_intake_map", show: true },
        { Header: "Engine Load", accessor: "can_engine_load_level", show: true },
        { Header: "Throttle Level", accessor: "can_throttle_pedal_level", show: true },
        { Header: "Intake Air Temp", accessor: "convert_can_intake_air_temperature", show: true },
        { Header: "Coolant Temp", accessor: "convert_can_engine_coolant_temperature", show: true },
        // { Header: "Vehicel vin", accessor: "vehicle_vin", show: true },
        { Header: "Place Location", accessor: "place_location", show: true },
        { Header: "Place Type", accessor: "place_type", show: true },
        { Header: "Timestamp", accessor: "timestamp", show: true },
        {
            Header: "actions",
            disableSortBy: true,
            accessor: "actions",
            show: true,
        },
    ];

    const openLogs = (data, type) => {
        if (data && type) {
            setRawDataValue(data[type] ?? '')
            setJsonTree(true)
        }
    }

    const clickInfoHandler = (data) => {
        if (data.start_group || data.end_group) {
            setSelectedRouteGroup({
                start: data?.start_group,
                end: data?.end_group,
                route_id: data.route_id,
                analysis: data?.RouteAnalysis,
                startRouteLog: data?.StartLog,
                endRouteLog: data?.EndLog
            })
        } else {
            window.alert('No data')
        }
    }


    const clickRouteViewHandler = async (data) => {
        try {
            let resp = await CrudService.getTrackingPoint(data.route_id)
            if (resp?.success) {
                data.RouteTrackingPoints = resp.data
                setHighlightRouteData([{ ...data, selected: true }])
                setOpenViewModal(true)
            } else {
                toast.error('Something went wrong!')
            }
            // page.current?.scrollIntoView({ behavior: "smooth" });
        } catch (error) {
            console.error('clickRouteViewHandler error', error)
        }
    }
    const clickSubRouteViewHandler = async (data) => {
        try {
            if (data) {
                setSelectedLogInfo(data.DeviceMonitorParameterValue)
            }
        } catch (error) {
            console.error('clickRouteViewHandler error', error)
        }
    }

    const closeRouteMap = () => {
        setHighlightRouteData([])
        setOpenViewModal(false)
    }

    const clickExportHandler = async (id) => {
        try {
            const exportUrl = `${process.env.REACT_APP_API_URL}/modules/export_excel/${id}`;
            window.open(exportUrl, '_blank');
        } catch (error) {
            console.error('clickExportHandler error', error)
        }
    }

    const clickRouteDeleteHandler = async (e, del_id) => {
        try {
            if (!confirm('Are you sure you want to delete this route?')) {
                e.nativeEvent.stopImmediatePropagation()
            } else {
                await CrudService.deleteRoutes(del_id)
                const response = await CrudService.getAllRoutes({
                    page: page + 1,
                    rowsPerPage,
                    search: searchQuery
                })
                setData(response.data)
                setNotification({
                    value: true,
                    text: 'The Route has been successfully removed',
                })
            }
        } catch (err) {
            console.error(err)
            setNotification({
                value: true,
                text: err.error,
            })
            return null
        }
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            {notification.value && (
                <MDAlert color="info" my="20px">
                    <MDTypography variant="body2" color="white">
                        {notification.text || ''}
                    </MDTypography>
                </MDAlert>
            )}
            <MDBox pt={1}>
                <MDBox>
                    <Card>
                        {
                            !isLoading ?
                                <MDBox>
                                    <MDBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px' }}>
                                        <MDBox></MDBox>
                                        <MDInput
                                            type="text"
                                            label="Search"
                                            variant="outlined"
                                            onChange={(e) => searchHandler(e.target.value)}
                                            value={searchQuery}
                                        />
                                    </MDBox>
                                    <MDBox sx={{height: 'calc(100vh - 5rem)'}} >
                                        <RouteCollapsibleTable
                                            data={getRows(data)}
                                            columns={routeColumns}
                                            subColumns={routeSubColumns}
                                            clickInfoHandler={clickInfoHandler}
                                            clickViewHandler={clickRouteViewHandler}
                                            clickSubRouteViewHandler={clickSubRouteViewHandler}
                                            clickExportHandler={clickExportHandler}
                                            clickDeleteHandler={clickRouteDeleteHandler}
                                            openLogs={openLogs}
                                            page={page}
                                            setPage={setPage}
                                            rowsPerPage={rowsPerPage}
                                            setRowsPerPage={setRowsPerPage}
                                            sortModel={sortModel}
                                            onSortModelChange={setSortModel}
                                            filterModel={filterModel}
                                            onFilterModelChange={setFilterModel}
                                            count={countRef.current}
                                        />
                                    </MDBox>
                                </MDBox> :
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                                        <Skeleton animation="wave" variant="rectangular" height="40px" width="12%" sx={{ margin: '5px', textAlign: 'right' }} />
                                    </Box>
                                    <Skeleton animation="wave" variant="rectangular" height="100px" sx={{ margin: '5px' }} />
                                    <Skeleton animation="wave" variant="rectangular" height="100px" sx={{ margin: '5px' }} />
                                    <Skeleton animation="wave" variant="rectangular" height="100px" sx={{ margin: '5px' }} />
                                    <Skeleton animation="wave" variant="rectangular" height="100px" sx={{ margin: '5px' }} />
                                    <Skeleton animation="wave" variant="rectangular" height="100px" sx={{ margin: '5px' }} />
                                </Box>
                        }
                    </Card>
                    <RouteModal selectedRouteGroup={selectedRouteGroup} setSelectedRouteGroup={setSelectedRouteGroup} />
                </MDBox>
            </MDBox>
            <JsonTree open={selectedLogInfo} onClose={() => setSelectedLogInfo(null)} data={selectedLogInfo} darkTheme={true} />
            <JsonTree open={jsonTree} onClose={() => setJsonTree(false)} data={rawDataValue} />
            <Modal
                open={openViewModal}
                onClose={closeRouteMap}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Card
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "60%", // Set the desired width here
                        backgroundColor: "white",
                        boxShadow: 24,
                        p: 4,
                        maxHeight: "70vh", // Set max height to enable scrolling
                        overflowY: "auto",
                        overflowX: "hidden",
                    }}
                >
                    <CardContent>
                        <RouteMap routeData={highlightRouteData} />
                    </CardContent>
                </Card>
            </Modal>
            <Footer />
        </DashboardLayout>
    )
}

export default Routes
